import { useFormStore } from '@/lib/formStore';
import { eventBusHub, type BusEvent } from '@/lib/eventBusHub';

// ============================================================================
// formApi.ts — ЕДИНСТВЕННЫЙ доступ к значениям ФОРМЫ (SSoT/SRP, по плану C1).
//
// Любое изменение значения поля формы (ввод, пикер, подчинённый список, модуль
// формы, авто-заполнение) идёт через setFormValue: пишет в formStore (единый
// источник для рендера и сохранения) И публикует событие в шину
// (form.value_changed) — observer видит всё, подписчики (другие поля, ТЧ,
// зависимые виджеты) реагируют. Никаких прямых проп-цепочек setValues.
//
// Контракт событий формы (все topic='form.*', payload — объект):
//   form.open            { formId, objectType, objectName, recordId }   — форма открыта
//   form.value_changed   { formId, attribute, value }                    — изменилось значение
//   form.tabular_changed { formId, tsName, rows }                        — изменилась ТЧ
//   form.initialized     { formId, values }                              — стор заполнен данными (1 раз)
//   form.save            { formId }                                      — запрошено сохранение
//   form.saved           { formId, recordId }                            — сохранено
// ============================================================================

export const FORM_EVENT = {
  open: 'form.open',
  valueChanged: 'form.value_changed',
  tabularChanged: 'form.tabular_changed',
  initialized: 'form.initialized',
  save: 'form.save',
  saved: 'form.saved',
} as const;

export interface FormValueEvent {
  formId: string;
  attribute: string;
  value: unknown;
}

/** Запись значения поля формы: стор (единый источник) + событие в шину. */
export function setFormValue(formId: string, attribute: string, value: unknown): void {
  if (attribute === 'Адрес') {
    void import('@/lib/eventLog').then((m) => m.eventLog.warning('ui', `[setFormValue] Адрес => ${String(value).slice(0, 40)}`));
  }
  useFormStore.getState().setValue(formId, attribute, value);
  eventBusHub.emit(FORM_EVENT.valueChanged, { formId, attribute, value } satisfies FormValueEvent);
}

/** Запись значения формы БЕЗ публикации события (инициализация данными из
 *  сервера при открытии — это НЕ пользовательское изменение). */
export function setFormValuesSilent(formId: string, values: Record<string, unknown>): void {
  useFormStore.getState().setForm(formId, values);
}

/** Завершение СЕАНСА формы (закрытие): снимает флаг инициализации; значения
 *  НОВОЙ записи очищаются (следующее создание — чистое), значения существующей
 *  записи СОХРАНЯЮТСЯ (повторное открытие не теряет несохранённый ввод).
 *  ЕДИНСТВЕННАЯ точка завершения сеанса — вызывается при закрытии формы. */
export function endFormSession(formId: string): void {
  useFormStore.getState().endSession(formId);
}

/** Подписка на события конкретной формы (автоотписка при размонтировании). */
export function subscribeFormEvents(formId: string, handler: (ev: BusEvent) => void): () => void {
  return eventBusHub.subscribe((ev) => {
    if (!ev.topic.startsWith('form.') || !formId) return;
    const payloadFormId = (ev.payload as any)?.formId;
    if (payloadFormId && payloadFormId !== formId) return;
    handler(ev);
  });
}
